/**
 * Design Tokens - Tokens de diseño corporativo
 * Define todos los valores de diseño reutilizables de la aplicación
 */

// ============================================
// PALETA DE COLORES CORPORATIVA
// ============================================
export const colors = {
  // Color Principal (Deep Tech Blue)
  primary: {
    main: '#0B3D91',
    light: '#1E5BB8',
    dark: '#082A6B',
    contrastText: '#FFFFFF',
  },

  // Acento Principal (Teal Innovation)
  accent: {
    main: '#00B5A6',
    light: '#33C4B8',
    dark: '#008B7F',
    contrastText: '#FFFFFF',
  },

  // Acento Secundario (Violet-Edge) - Usar con moderación
  accentSecondary: {
    main: '#7C4DFF',
    light: '#9C7AFF',
    dark: '#5E3AE6',
    contrastText: '#FFFFFF',
  },

  // Texto Principal (Dark Slate)
  text: {
    primary: '#0F1724',
    secondary: '#64748B',
    disabled: '#94A3B8',
    hint: '#CBD5E1',
  },

  // Fondos
  background: {
    default: '#F5F8FB', // Soft Cloud
    paper: '#FFFFFF',
    elevated: '#FFFFFF',
    dark: '#0F1724',
  },

  // Neutral / Border (Cool Gray)
  neutral: {
    main: '#E6EAEE',
    light: '#F1F5F9',
    dark: '#CBD5E1',
  },

  // Colores semánticos (basados en la paleta corporativa)
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#00B5A6', // Usar el acento principal para info
    light: '#33C4B8',
    dark: '#008B7F',
    contrastText: '#FFFFFF',
  },

  // ============================================
  // PALETA DE COLORES PARA BADGES
  // Colores vibrantes y llamativos para badges, alineados con identidad corporativa
  // Sistema semáforo: Verde (éxito), Rojo (error), Naranja (advertencia)
  // ============================================
  badge: {
    // Verde - Éxito, Activo, Verificado (Sistema Semáforo: VERDE = BIEN)
    success: {
      main: '#10B981',      // Verde esmeralda vibrante (éxito/activo)
      light: '#34D399',    // Verde más claro para gradientes
      dark: '#059669',      // Verde más oscuro para hover
      contrastText: '#FFFFFF',
      // Variante más saturada para mayor visibilidad
      vibrant: '#06D6A0',   // Verde más brillante y llamativo
    },
    
    // Rojo - Error, Inactivo, Rechazado (Sistema Semáforo: ROJO = MAL)
    error: {
      main: '#EF4444',      // Rojo coral vibrante (error/inactivo)
      light: '#F87171',     // Rojo más claro para gradientes
      dark: '#DC2626',      // Rojo más oscuro para hover
      contrastText: '#FFFFFF',
      // Variante más saturada para mayor visibilidad
      vibrant: '#F43F5E',   // Rojo más brillante y llamativo
    },
    
    // Naranja/Amarillo - Advertencia, Pendiente, No Verificado (Sistema Semáforo: NARANJA = ATENCIÓN)
    warning: {
      main: '#F59E0B',      // Naranja ámbar (advertencia/pendiente)
      light: '#FBBF24',     // Naranja más claro para gradientes
      dark: '#D97706',      // Naranja más oscuro para hover
      contrastText: '#FFFFFF',
      // Variante más saturada para mayor visibilidad
      vibrant: '#FB923C',   // Naranja más brillante y llamativo
    },
    
    // Azul - Información, Neutral, Sistema (Basado en Primary corporativo)
    info: {
      main: '#0B3D91',      // Deep Tech Blue (información/neutral)
      light: '#1E5BB8',     // Azul más claro para gradientes
      dark: '#082A6B',      // Azul más oscuro para hover
      contrastText: '#FFFFFF',
      // Variante más clara para badges informativos
      vibrant: '#2563EB',   // Azul más brillante y llamativo
    },
    
    // Teal - Accent corporativo para roles especiales
    accent: {
      main: '#00B5A6',      // Teal Innovation (roles admin)
      light: '#33C4B8',    // Teal más claro
      dark: '#008B7F',     // Teal más oscuro
      contrastText: '#FFFFFF',
      vibrant: '#14B8A6',   // Teal más brillante
    },
    
    // Violeta - Accent secundario para roles premium
    accentSecondary: {
      main: '#7C4DFF',     // Violet-Edge (roles super admin)
      light: '#9C7AFF',    // Violeta más claro
      dark: '#5E3AE6',     // Violeta más oscuro
      contrastText: '#FFFFFF',
      vibrant: '#8B5CF6',  // Violeta más brillante
    },
    
    // Gris - Solo para casos muy específicos (sistema, deshabilitado)
    neutral: {
      main: '#64748B',     // Slate gris (solo para casos especiales)
      light: '#94A3B8',    // Gris más claro
      dark: '#475569',     // Gris más oscuro
      contrastText: '#FFFFFF',
      // Para fondos sutiles cuando sea necesario
      subtle: '#E2E8F0',   // Gris muy claro para fondos
    },
  },
};

// ============================================
// TIPOGRAFÍA CORPORATIVA
// ============================================
export const typography = {
  // Fuente principal: Inter (UI & Texto)
  fontFamily: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),

  // Fuente display: Poppins (Headlines & Wordmark)
  fontFamilyDisplay: [
    'Poppins',
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'sans-serif',
  ].join(','),

  // Pesos de fuente
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

// ============================================
// ESPACIADO
// ============================================
export const spacing = {
  unit: 6.4, // Base de 6.4px (reducido al 80%)
  xs: 3.2,
  sm: 6.4,
  md: 12.8,
  lg: 19.2,
  xl: 25.6,
  xxl: 38.4,
  xxxl: 51.2,
};

// ============================================
// FORMAS Y BORDES
// ============================================
export const shape = {
  borderRadius: {
    none: 0,
    small: 2,    // Reducido de 8px a 2px (25%)
    medium: 3,   // Reducido de 12px a 3px (25%)
    large: 4,    // Reducido de 16px a 4px (25%)
    xlarge: 6,   // Reducido proporcionalmente
    round: '50%',
  },
  borderWidth: {
    thin: 1,
    medium: 2,
    thick: 3,
  },
};

// ============================================
// SOMBRAS PERSONALIZADAS
// ============================================
export const shadows = [
  'none',
  '0 1px 3px rgba(11, 61, 145, 0.08)',
  '0 2px 6px rgba(11, 61, 145, 0.1)',
  '0 4px 12px rgba(11, 61, 145, 0.12)',
  '0 8px 24px rgba(11, 61, 145, 0.15)',
  '0 12px 32px rgba(11, 61, 145, 0.18)',
  '0 16px 48px rgba(11, 61, 145, 0.2)',
  '0 20px 64px rgba(11, 61, 145, 0.22)',
  '0 24px 80px rgba(11, 61, 145, 0.24)',
  '0 28px 96px rgba(11, 61, 145, 0.26)',
  '0 32px 112px rgba(11, 61, 145, 0.28)',
  '0 36px 128px rgba(11, 61, 145, 0.3)',
  '0 40px 144px rgba(11, 61, 145, 0.32)',
  '0 44px 160px rgba(11, 61, 145, 0.34)',
  '0 48px 176px rgba(11, 61, 145, 0.36)',
  '0 52px 192px rgba(11, 61, 145, 0.38)',
  '0 56px 208px rgba(11, 61, 145, 0.4)',
  '0 60px 224px rgba(11, 61, 145, 0.42)',
  '0 64px 240px rgba(11, 61, 145, 0.44)',
  '0 68px 256px rgba(11, 61, 145, 0.46)',
  '0 72px 272px rgba(11, 61, 145, 0.48)',
  '0 76px 288px rgba(11, 61, 145, 0.5)',
  '0 80px 304px rgba(11, 61, 145, 0.52)',
  '0 84px 320px rgba(11, 61, 145, 0.54)',
  '0 88px 336px rgba(11, 61, 145, 0.56)',
] as const;

// ============================================
// GRADIENTES CORPORATIVOS
// ============================================
export const gradients = {
  primary: 'linear-gradient(135deg, #0B3D91 0%, #1E5BB8 100%)',
  primaryReverse: 'linear-gradient(135deg, #1E5BB8 0%, #0B3D91 100%)',
  accent: 'linear-gradient(135deg, #00B5A6 0%, #33C4B8 100%)',
  accentSecondary: 'linear-gradient(135deg, #7C4DFF 0%, #9C7AFF 100%)',
  overlay: 'linear-gradient(180deg, rgba(15, 23, 36, 0) 0%, rgba(15, 23, 36, 0.7) 100%)',
  overlayLight: 'linear-gradient(180deg, rgba(245, 248, 251, 0) 0%, rgba(245, 248, 251, 0.95) 100%)',
  hero: 'linear-gradient(135deg, #0B3D91 0%, #00B5A6 50%, #7C4DFF 100%)',
};

// ============================================
// BREAKPOINTS
// ============================================
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// ============================================
// Z-INDEX LAYERS
// ============================================
export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  appBar: 1100,
  drawer: 1200,
  snackbar: 1400,
};

// ============================================
// TRANSICIONES
// ============================================
export const transitions = {
  duration: {
    shortest: 150,
    shorter: 200,
    short: 250,
    standard: 300,
    complex: 375,
    enteringScreen: 225,
    leavingScreen: 195,
  },
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },
};

// ============================================
// RUTAS DE LOGOS
// ============================================
export const logoPaths = {
  light: '/assets/logos/logo-light.svg', // Para fondos oscuros
  dark: '/assets/logos/logo-dark.svg',    // Para fondos claros
  // También soportar PNG como fallback
  lightPng: '/assets/logos/logo-light.png',
  darkPng: '/assets/logos/logo-dark.png',
};

// ============================================
// EXPORTACIÓN
// ============================================
export default {
  colors,
  typography,
  spacing,
  shape,
  shadows,
  gradients,
  breakpoints,
  zIndex,
  transitions,
  logoPaths,
};





